/**
 * Shop Pagination block entry.
 *
 * Importing the SCSS here makes Vite bundle the block's CSS alongside its JS.
 *
 * The control works without this file — it is a link to page 2. What this adds
 * is fetching that page and appending it, so the reader keeps what they have
 * already scrolled past instead of losing it to a page load.
 *
 * It shares no file with the filter bar, which is the other thing on this page
 * that talks to the same endpoint. They coordinate through one DOM event
 * instead: the filters announce a new result set, and this resets to page one.
 * Two blocks, one conversation, no shared module.
 */

import './index.scss';

const pager = document.querySelector('.shop-pagination');
const grid = document.querySelector('.shop-archive__items');

/**
 * Read the current filter selection straight out of the address bar.
 *
 * The URL is the state — the filter bar puts it there, and a reader who arrived
 * on a shared link has it without any block having run. Asking the DOM for what
 * is ticked would work only in the first case.
 *
 * @returns {URLSearchParams} The query, without any paging of its own.
 */
function selection() {
    const params = new URLSearchParams(window.location.search);

    params.delete('page');
    params.delete('paged');

    /* The archive itself, which the address bar does not carry on a category or
     * collection page — it is in the path. Without it the second page came back
     * from the whole catalogue, so scrolling a sofa listing produced beds.
     *
     * Merged under whatever the query string says, never over it: a filter the
     * reader picked is theirs, the archive is where they picked it. */
    let context = {};

    try {
        context = JSON.parse(pager?.dataset.context ?? '{}');
    } catch {
        context = {};
    }

    for (const [key, value] of Object.entries(context)) {
        if (!value) {
            continue;
        }

        const already = params.get(key);

        params.set(
            key,
            already
                ? [...new Set([...already.split(','), ...String(value).split(',')])].join(',')
                : String(value)
        );
    }

    return params;
}

/**
 * Whether the control is on screen, counting the margin the observer uses.
 *
 * @param {HTMLElement} node The pagination element.
 * @returns {boolean} True when it is visible or nearly so.
 */
function inView(node) {
    const box = node.getBoundingClientRect();
    const margin = window.innerHeight * 0.4;

    return box.top < window.innerHeight + margin && box.bottom > -margin;
}

/**
 * Redraw the bar and the sentence from the block's own state.
 *
 * @param {HTMLElement} node The pagination element.
 * @returns {void}
 */
function paint(node) {
    const shown = Number(node.dataset.shown);
    const total = Number(node.dataset.total);

    const fill = node.querySelector('.shop-pagination__bar-fill');
    const showing = node.querySelector('.shop-pagination__showing');
    const more = node.querySelector('.shop-pagination__more');

    if (fill) {
        fill.style.setProperty(
            '--shop-pagination-progress',
            `${total ? Math.round((shown / total) * 100) : 100}%`
        );
    }

    if (showing) {
        /* The sentence's wording is the server's — it arrives as a pattern with
         * two placeholders, already translated. Filling those keeps the word
         * order and the thousands separator of whatever locale the site runs
         * in, which searching the finished sentence for its numbers would not. */
        const numbers = [shown, total];

        showing.textContent = (showing.dataset.pattern ?? '%1$s / %2$s').replace(
            /%(\d)\$s/g,
            (_, position) => numbers[Number(position) - 1]?.toLocaleString() ?? ''
        );
    }

    if (more) {
        more.hidden = shown >= total;
    }
}

/**
 * Fetch the next page and append it.
 *
 * @param {HTMLElement} node The pagination element.
 * @returns {Promise<void>}
 */
async function loadMore(node) {
    /* Nothing left to fetch. Checked before the request, not after: the
     * observer fires as soon as the control scrolls into view, and on a shop
     * short enough to show it immediately that is on page load. */
    if (Number(node.dataset.shown) >= Number(node.dataset.total)) {
        return;
    }

    if ('true' === node.dataset.busy || !grid) {
        return;
    }

    const next = Number(node.dataset.page) + 1;
    const params = selection();

    params.set('page', String(next));
    params.set('per_page', node.dataset.perPage);

    node.dataset.busy = 'true';

    const more = node.querySelector('.shop-pagination__more');
    const resting = more?.textContent ?? '';

    if (more) {
        more.setAttribute('aria-busy', 'true');
    }

    try {
        const response = await fetch(`${node.dataset.endpoint}?${params}`, {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
        });
        const payload = await response.json();

        if (!response.ok || 'string' !== typeof payload.html) {
            throw new Error('unexpected response');
        }

        /* Parsed and appended rather than `innerHTML +=`, which would rebuild
         * every card already on the page — losing their images to a second
         * download and any state the reader had. */
        const parsed = document.createRange().createContextualFragment(payload.html);
        const added = parsed.children.length;

        grid.append(parsed);

        node.dataset.page = String(payload.page ?? next);
        node.dataset.total = String(payload.total ?? node.dataset.total);
        node.dataset.shown = String(Number(node.dataset.shown) + added);

        paint(node);

        /* Re-arm. An IntersectionObserver reports the *crossing*, not the
         * state: once the control is in view it stays in view, so appending a
         * page below it produces no second callback and the scroll stops one
         * page in. Asking again while it is still on screen is what makes it
         * infinite. */
        if (inView(node) && Number(node.dataset.shown) < Number(node.dataset.total)) {
            requestAnimationFrame(() => loadMore(node));
        }
    } catch {
        /* Hand it back to the browser: the link still points at page 2, and a
         * page load is a worse experience than this, not a broken one. */
        window.location.href = more?.getAttribute('href') ?? window.location.href;
        return;
    } finally {
        node.dataset.busy = 'false';

        if (more) {
            more.removeAttribute('aria-busy');
            more.textContent = resting;
        }
    }
}

if (pager) {
    const more = pager.querySelector('.shop-pagination__more');

    more?.addEventListener('click', (event) => {
        event.preventDefault();
        loadMore(pager);
    });

    /*
     * The part of infinite scroll worth having: when the control is nearly in
     * view, fetch without waiting to be asked. The button stays — it is what
     * the keyboard reaches, and what still works if this never fires — so
     * nothing depends on the observer being supported.
     */
    if ('IntersectionObserver' in window) {
        const watcher = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        loadMore(pager);
                    }
                }
            },
            { rootMargin: '0px 0px 40% 0px' }
        );

        watcher.observe(pager);
    }

    /*
     * A new filter selection is a new result set: page one, a new total, and
     * whatever the filter bar has already put on screen.
     */
    document.addEventListener('shop:filtered', (event) => {
        pager.dataset.page = String(event.detail?.page ?? 1);
        pager.dataset.total = String(event.detail?.total ?? 0);
        pager.dataset.shown = String(event.detail?.shown ?? 0);

        paint(pager);
    });
}
